import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Alert,
    Chip,
    Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import apiService from '../services/api';
import ChartRenderer from './ChartRenderer';
import { extractChartData } from '../utils/pdfExport';

/**
 * In-window chat panel that talks to a chat session initialized with the
 * current report. The assistant can live-edit the report via the backend
 * `update_report` tool; when it does, the response includes `updated_report`
 * which we hand back to the parent through `onReportUpdated` so the canvas
 * re-renders instantly.
 */
const ChatPanel = ({ report, country, year, reportSessionId, onReportUpdated, onClose }) => {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [currentMessage, setCurrentMessage] = useState('');
    const [initializing, setInitializing] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const messagesEndRef = useRef(null);

    // Initialize a report-aware chat session when the panel mounts.
    // The parent re-mounts this component (via `key`) when the report changes.
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                setInitializing(true);
                setError('');
                const response = await apiService.initializeChat(report, country, year, reportSessionId);
                if (cancelled) return;

                if (response.data.session_id) {
                    const newSessionId = response.data.session_id;
                    setSessionId(newSessionId);
                    // Load the welcome message created on the backend
                    try {
                        const msgRes = await apiService.getChatSessionMessages(newSessionId);
                        if (!cancelled) {
                            setMessages(
                                (msgRes.data.messages || []).map((m) => ({
                                    role: m.role,
                                    content: m.content,
                                    timestamp: m.timestamp
                                }))
                            );
                        }
                    } catch (msgErr) {
                        console.error('Error loading initial chat messages:', msgErr);
                    }
                } else {
                    setError('Failed to start a chat for this report.');
                }
            } catch (err) {
                console.error('Error initializing report chat:', err);
                if (!cancelled) setError('Failed to start a chat for this report.');
            } finally {
                if (!cancelled) setInitializing(false);
            }
        };

        init();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const sendMessage = async () => {
        if (!currentMessage.trim() || !sessionId || loading) {
            return;
        }

        const userMessage = currentMessage.trim();
        setCurrentMessage('');
        setLoading(true);
        setError('');

        setMessages((prev) => [
            ...prev,
            { role: 'user', content: userMessage, timestamp: new Date().toISOString() }
        ]);

        try {
            const response = await apiService.sendChatMessage(sessionId, userMessage, country);

            if (response.data.response) {
                const reportWasEdited = !!response.data.updated_report;
                setMessages((prev) => [
                    ...prev,
                    {
                        role: 'assistant',
                        content: response.data.response,
                        timestamp: new Date().toISOString(),
                        reportEdited: reportWasEdited
                    }
                ]);

                // Push live report edits up to the canvas
                if (reportWasEdited && onReportUpdated) {
                    onReportUpdated(response.data.updated_report);
                }
            } else {
                setError('No response received from the assistant.');
            }
        } catch (err) {
            console.error('Error sending chat message:', err);
            const errorMessage =
                err.response?.data?.detail || 'Failed to send message. Please try again.';
            setMessages((prev) => [
                ...prev,
                {
                    role: 'assistant',
                    content: `I'm sorry, I encountered an error: ${errorMessage}`,
                    timestamp: new Date().toISOString(),
                    isError: true
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const renderAssistantContent = (content) => {
        const charts = extractChartData(content);
        if (charts.length === 0) {
            return <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>;
        }

        let lastIndex = 0;
        const parts = [];
        charts.forEach((chart, index) => {
            if (chart.startIndex > lastIndex) {
                parts.push(
                    <ReactMarkdown key={`text-${index}`} remarkPlugins={[remarkGfm]}>
                        {content.substring(lastIndex, chart.startIndex)}
                    </ReactMarkdown>
                );
            }
            parts.push(
                <Box key={`chart-${index}`} sx={{ my: 2 }}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <BarChartIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography variant="subtitle2" fontWeight="bold">
                                {chart.chartData.title}
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <ChartRenderer visualization={chart.chartData} />
                    </Paper>
                </Box>
            );
            lastIndex = chart.endIndex;
        });
        if (lastIndex < content.length) {
            parts.push(
                <ReactMarkdown key="text-end" remarkPlugins={[remarkGfm]}>
                    {content.substring(lastIndex)}
                </ReactMarkdown>
            );
        }
        return <>{parts}</>;
    };

    return (
        <Paper
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden'
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    flexShrink: 0
                }}
            >
                <SmartToyIcon sx={{ mr: 1 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        Chat with this report
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        Ask questions or request edits — I can revise the report directly.
                    </Typography>
                </Box>
                <Tooltip title="Close chat">
                    <IconButton size="small" onClick={onClose} sx={{ color: 'inherit' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Messages */}
            <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
                {initializing ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={22} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Loading the report into the chat…
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        {messages.map((message, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        bgcolor: message.role === 'user' ? 'primary.light' : 'grey.50',
                                        color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                        {message.role === 'user' ? (
                                            <PersonIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                        ) : (
                                            <SmartToyIcon sx={{ mr: 0.5, fontSize: 18 }} />
                                        )}
                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                            {message.role === 'user' ? 'You' : 'Assistant'}
                                        </Typography>
                                        {message.reportEdited && (
                                            <Chip
                                                icon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
                                                label="Report updated"
                                                size="small"
                                                color="success"
                                                sx={{ ml: 'auto', height: 20, fontSize: '0.65rem' }}
                                            />
                                        )}
                                    </Box>
                                    {message.role === 'assistant' ? (
                                        <Box
                                            sx={{
                                                fontSize: '0.9rem',
                                                color: message.isError ? 'error.main' : 'inherit',
                                                '& p': { my: 0.5 },
                                                '& ul, & ol': { my: 0.5, pl: 2.5 },
                                                '& table': { borderCollapse: 'collapse', width: '100%' },
                                                '& th, & td': { border: '1px solid #ddd', p: 0.5, fontSize: '0.8rem' }
                                            }}
                                        >
                                            {renderAssistantContent(message.content)}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {message.content}
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                        {loading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Assistant is thinking…
                                </Typography>
                            </Box>
                        )}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Box>

            {/* Input */}
            <Box sx={{ p: 1.5, borderTop: '1px solid #eee', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        fullWidth
                        multiline
                        maxRows={4}
                        value={currentMessage}
                        onChange={(e) => setCurrentMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Ask about or edit this report…"
                        disabled={loading || initializing || !sessionId}
                        variant="outlined"
                        size="small"
                    />
                    <Button
                        variant="contained"
                        onClick={sendMessage}
                        disabled={loading || initializing || !currentMessage.trim() || !sessionId}
                        sx={{ minWidth: 'auto', px: 2 }}
                    >
                        {loading ? <CircularProgress size={20} /> : <SendIcon />}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default ChatPanel;
